import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import EmojiFace from "./EmojiFace";
import { createAudioPlayer } from "./audio";
import {
  currentStep,
  initialState,
  isDoneScreen,
  categoryTitle,
  progress,
  questionSteps,
  surveyReducer,
} from "./surveyState";
import "./SurveyPlayer.css";

const PHASE_LABEL = { pre: "Pre-test", post: "Post-test" };

function SpeakerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false">
      <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
      <path d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ListenButton({ playing, onClick, label = "Listen again", large = false }) {
  return (
    <button
      type="button"
      className={`r21-survey__listen${large ? " r21-survey__listen--large" : ""}${playing ? " is-playing" : ""}`}
      onClick={onClick}
      aria-label={label}
    >
      <SpeakerIcon />
      {large && <span>{playing ? "Listening…" : "Listen"}</span>}
    </button>
  );
}

function ScalePreview({ choices }) {
  return (
    <ul className="r21-survey__scale-preview" aria-label="Answer faces">
      {choices.map((c) => (
        <li key={c.value}>
          <EmojiFace face={c.face} size={56} />
          <span className="r21-survey__scale-value">{c.value}</span>
          <span className="r21-survey__scale-label">{c.label}</span>
        </li>
      ))}
    </ul>
  );
}

function Choices({ choices, selected, savingValue, disabled, onChoose, questionText }) {
  const refs = useRef([]);
  const focusIndex = Math.max(0, choices.findIndex((c) => c.value === selected));

  const onKeyDown = (event, i) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (step) {
      event.preventDefault();
      const next = (i + step + choices.length) % choices.length;
      refs.current[next]?.focus();
    }
  };

  return (
    <div className="r21-survey__choices" role="radiogroup" aria-label={questionText}>
      {choices.map((choice, i) => {
        const isSelected = selected === choice.value;
        return (
          <button
            key={choice.value}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={`${choice.value}, ${choice.label}`}
            tabIndex={i === focusIndex ? 0 : -1}
            disabled={disabled}
            className={[
              "r21-survey__choice",
              isSelected && "is-selected",
              selected !== undefined && !isSelected && "is-dimmed",
              savingValue === choice.value && "is-saving",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => onChoose(choice.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            <span className="r21-survey__choice-face">
              <EmojiFace face={choice.face} size={88} />
            </span>
            <span className="r21-survey__choice-value">{choice.value}</span>
            <span className="r21-survey__choice-label">{choice.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 *
 * @param {object}   props.session        from the backend's start call: { survey, steps, answers, resumeAt, completed }
 * @param {Function} props.onAnswer       async (questionId, value) => void; throw to show a retry message
 * @param {Function} props.onComplete     async () => void
 * @param {Function} [props.onExit]       leave the survey (answers are already saved)
 * @param {Function} [props.resolveAudioUrl]  maps an audio key to a playable URL
 * @param {object}   [props.audioPlayer]  { play(url): Promise, stop() }; defaults to HTML audio
 * @param {number}   [props.autoAdvanceMs] pause after choosing before the next question
 */
export default function SurveyPlayer({
  session,
  onAnswer,
  onComplete,
  onExit,
  resolveAudioUrl,
  audioPlayer,
  autoAdvanceMs = 700,
}) {
  const { survey } = session;
  const choices = survey.scale.choices;
  const [state, dispatch] = useReducer(surveyReducer, session, initialState);
  const [playing, setPlaying] = useState(false);
  const [completing, setCompleting] = useState(false);
  const player = useMemo(
    () => audioPlayer ?? createAudioPlayer({ resolveUrl: resolveAudioUrl }),
    [audioPlayer, resolveAudioUrl],
  );
  const advanceTimer = useRef(null);
  const step = currentStep(state);
  const { answered, total } = progress(state);
  const questions = questionSteps(state);

  const play = useCallback(
    (src) => {
      if (!src) return;
      setPlaying(true);
      player.play(src).then(() => setPlaying(false));
    },
    [player],
  );

  // Play each step's prompt when it appears.
  useEffect(() => {
    if (step?.audioUrl) play(step.audioUrl);
    return () => {
      player.stop();
      setPlaying(false);
    };
  }, [state.index]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  const choose = async (value) => {
    if (!step || step.type !== "question" || state.saving) return;
    clearTimeout(advanceTimer.current);
    const questionId = step.questionId;
    if (state.answers[questionId] !== value) {
      dispatch({ type: "saving", questionId, value });
      try {
        await onAnswer(questionId, value);
      } catch {
        dispatch({ type: "failed", message: "We couldn't save that answer. Tap your face again to retry." });
        return;
      }
      dispatch({ type: "saved", questionId, value });
    }
    player.stop();
    advanceTimer.current = setTimeout(() => dispatch({ type: "next" }), autoAdvanceMs);
  };

  // Number keys pick the matching answer on question screens.
  useEffect(() => {
    if (step?.type !== "question") return undefined;
    const onKey = (event) => {
      const value = Number(event.key);
      if (choices.some((c) => c.value === value)) choose(value);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const finish = async () => {
    setCompleting(true);
    try {
      await onComplete();
      dispatch({ type: "finished" });
    } catch {
      dispatch({ type: "failed", message: "We couldn't finish just now. Please tap Finish again." });
    } finally {
      setCompleting(false);
    }
  };

  const firstQuestionIndex = state.steps.findIndex((s) => s.type === "question");
  const canGoBack = !state.finished && state.index > firstQuestionIndex && firstQuestionIndex !== -1;

  let body;
  if (isDoneScreen(state)) {
    body = state.finished ? (
      <section className="r21-survey__panel r21-survey__panel--center r21-survey__done">
        <div className="r21-survey__burst" aria-hidden="true">
          <EmojiFace face="very_happy" size={128} />
        </div>
        <h1 className="r21-survey__heading">Thank you!</h1>
        <p className="r21-survey__lead">Your answers help us make reading better for you.</p>
        {onExit && (
          <button type="button" className="r21-survey__primary" onClick={onExit}>
            Done
          </button>
        )}
      </section>
    ) : (
      <section className="r21-survey__panel r21-survey__panel--center">
        <h1 className="r21-survey__heading">That's everything!</h1>
        <p className="r21-survey__lead">
          You answered {answered} of {total} questions.
        </p>
        <button type="button" className="r21-survey__primary" onClick={finish} disabled={completing || answered < total}>
          {completing ? "Saving…" : "Finish"}
        </button>
        {answered < total && (
          <button
            type="button"
            className="r21-survey__link"
            onClick={() =>
              dispatch({
                type: "goTo",
                index: state.steps.findIndex((s) => s.type === "question" && state.answers[s.questionId] === undefined),
              })
            }
          >
            Answer the questions you skipped
          </button>
        )}
      </section>
    );
  } else if (step.type === "instructions") {
    body = (
      <section className="r21-survey__panel r21-survey__panel--center">
        <ListenButton large playing={playing} onClick={() => play(step.audioUrl)} label="Listen to the instructions" />
        {step.text && <p className="r21-survey__instructions">{step.text}</p>}
        <ScalePreview choices={choices} />
        <button type="button" className="r21-survey__primary" onClick={() => dispatch({ type: "next" })}>
          Let's start
        </button>
      </section>
    );
  } else {
    const category = categoryTitle(state, step);
    body = (
      <section className="r21-survey__panel">
        <p className="r21-survey__eyebrow">
          Question {step.number} of {questions.length}
          {category && <span className="r21-survey__eyebrow-sep">{category}</span>}
        </p>
        <div className="r21-survey__question">
          <h1 className="r21-survey__question-text">{step.text}</h1>
          {step.audioUrl && <ListenButton playing={playing} onClick={() => play(step.audioUrl)} />}
        </div>
        <Choices
          choices={choices}
          selected={state.saving === step.questionId ? state.pendingValue : state.answers[step.questionId]}
          savingValue={state.saving === step.questionId ? state.pendingValue : undefined}
          disabled={Boolean(state.saving)}
          onChoose={choose}
          questionText={step.text}
        />
      </section>
    );
  }

  return (
    <div className="r21-survey" data-phase={survey.phase}>
      <header className="r21-survey__top">
        <div className="r21-survey__title">
          <span className="r21-survey__name">{survey.title}</span>
          {PHASE_LABEL[survey.phase] && <span className="r21-survey__chip">{PHASE_LABEL[survey.phase]}</span>}
        </div>
        <div className="r21-survey__progress" aria-label={`${answered} of ${total} questions answered`}>
          {questions.map((q) => (
            <span
              key={q.questionId}
              className={[
                "r21-survey__segment",
                state.answers[q.questionId] !== undefined && "is-done",
                step?.questionId === q.questionId && "is-current",
              ]
                .filter(Boolean)
                .join(" ")}
            />
          ))}
        </div>
        {onExit && !state.finished && (
          <button type="button" className="r21-survey__exit" onClick={onExit}>
            Save &amp; exit
          </button>
        )}
      </header>

      <main className="r21-survey__stage" key={state.index}>
        {body}
      </main>

      <footer className="r21-survey__footer">
        {canGoBack && (
          <button type="button" className="r21-survey__back" onClick={() => dispatch({ type: "back" })}>
            <BackIcon />
            Back
          </button>
        )}
        <div className="r21-survey__status" role="status" aria-live="polite">
          {state.error ? <span className="r21-survey__error">{state.error}</span> : state.saving ? "Saving…" : ""}
        </div>
      </footer>
    </div>
  );
}
